import { atom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { mutationAtomWithLoadable } from 'lib/atoms';
import request from 'lib/request';
import type { SearchResult } from 'types/api-responses';
import type { TableState } from 'components/data-table/types';
import type { EvalUnifiedResult } from './types';

// sessionStorage (not local) so a brand-new compare in a fresh tab starts empty,
// while the pair survives the current tab's navigation into the results page.
const selectedEvalsStorage = createJSONStorage<EvalUnifiedResult[]>(
  () => sessionStorage
);

/**
 * Holds the currently selected evaluations (max 2). Persisted in sessionStorage
 * so the pair survives the hard navigation into the results page (`window.location`)
 * and is restored when the results header "Edit" routes back here — otherwise the
 * fresh page load resets the in-memory store and the wizard would reopen with an
 * empty selection (both the checked rows and the Current Selections sidebar).
 */
export const selectedEvalsAtom = atomWithStorage<EvalUnifiedResult[]>(
  'compare:selected-evals',
  [],
  selectedEvalsStorage,
  { getOnInit: true }
);

/**
 * Mutation atom for fetching the unified evaluation list.
 * Accepts TableState and maps them to the backend API contract.
 * @returns SearchResult with evaluation data, page info, and totalCount
 */
const [evalFetchAtom, evalFetchLoadableAtom] = mutationAtomWithLoadable(
  async (
    _get,
    _set,
    { page, pageSize, sorting, globalFilter, columnFilters }: TableState
  ): Promise<SearchResult<EvalUnifiedResult>> => {
    const payload: Record<string, unknown> = { page, page_size: pageSize };

    if (globalFilter) {
      payload.text = globalFilter;
    }

    const sort = sorting[0];
    if (sort) {
      payload.sort_attribute = sort.id;
      payload.sort_direction = sort.desc ? 'DESC' : 'ASC';
    }

    columnFilters.forEach((f) => {
      if (f.id === 'acreage') {
        const [min, max] = f.value as [number | null, number | null];
        const range: Record<string, number> = {};
        if (min !== null && min !== undefined) range.min = min;
        if (max !== null && max !== undefined) range.max = max;
        if (Object.keys(range).length > 0) {
          payload.acreage = range;
        }
      } else {
        const val = f.value as string;
        if (val) {
          payload[f.id] = val;
        }
      }
    });

    const abortController = new AbortController();
    const response = await request.post<
      SearchResult<EvalUnifiedResult>,
      unknown
    >('/queries/package_evals_unified', payload, abortController);

    if (response.type === 'success') {
      return response.data;
    }

    if (response.type === 'abort') {
      throw new Error('Request aborted');
    }

    throw new Error(
      response.type === 'fatal' ? response.message : 'Request failed'
    );
  }
);

export { evalFetchAtom, evalFetchLoadableAtom };

/**
 * Persists the last TableState used to fetch evaluations.
 * Survives step unmount/remount so DataTable can restore its state
 * when the user navigates back from the Summary step.
 */
export const lastEvalParamsAtom = atom<TableState | null>(null);
